<template>
  <div class="device-screen">
    <div class="back-button">
      <el-button @click="navigateBack">
        返回主屏幕
      </el-button>
    </div>
    
    <div class="screen-title">
      设备管理
    </div>
    
    <el-row
      :gutter="20"
      class="data-cards"
    >
      <el-col :span="6">
        <el-card class="data-card">
          <h3>设备总数</h3>
          <div class="big-number">
            7068
          </div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card class="data-card">
          <h3>在线设备</h3>
          <div class="big-number">
            6500
          </div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card class="data-card">
          <h3>离线设备</h3>
          <div class="big-number">
            368
          </div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card class="data-card">
          <h3>设备完好率</h3>
          <div class="big-number">
            95.8%
          </div>
        </el-card>
      </el-col>
    </el-row>
    
    <el-row
      :gutter="20"
      class="charts-row"
    >
      <el-col :span="12">
        <el-card class="chart-card">
          <h3>设备类型分布</h3>
          <div class="chart-container">
            <div
              ref="deviceTypeChart"
              style="width: 100%; height: 300px;"
            />
          </div>
        </el-card>
      </el-col>
      <el-col :span="12">
        <el-card class="chart-card">
          <h3>设备状态分布</h3>
          <div class="chart-container">
            <div
              ref="deviceStatusChart"
              style="width: 100%; height: 300px;"
            />
          </div>
        </el-card>
      </el-col>
    </el-row>
    
    <el-row
      :gutter="20"
      class="charts-row"
    >
      <el-col :span="24">
        <el-card class="chart-card">
          <h3>设备在线率趋势</h3>
          <div class="chart-container">
            <div
              ref="deviceOnlineTrendChart"
              style="width: 100%; height: 300px;"
            />
          </div>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import * as echarts from 'echarts'

export default {
  name: 'DeviceScreen',
  setup() {
    const router = useRouter()
    const deviceTypeChart = ref(null)
    const deviceStatusChart = ref(null)
    const deviceOnlineTrendChart = ref(null)
    const chartInstances = {}
    const resizeListeners = []

    const navigateBack = () => {
      router.push({ name: 'MainDashboard' })
    }

    // 检查容器尺寸是否有效
    const hasValidSize = (container) => {
      if (!container) return false
      const rect = container.getBoundingClientRect()
      const valid = rect.width > 0 && rect.height > 0
      console.log(`容器尺寸: ${rect.width}x${rect.height}, 是否有效: ${valid}`)
      return valid
    }

    // 强制初始化图表
    const forceInitChart = (refName, option) => {
      return new Promise((resolve, reject) => {
        requestAnimationFrame(() => {
          nextTick(() => {
            const container = refName.value
            if (!container) {
              console.error('图表容器不存在')
              reject(new Error('图表容器不存在'))
              return
            }

            // 检查并强制设置容器尺寸
            if (!hasValidSize(container)) {
              console.warn('容器尺寸无效，尝试强制设置')
              container.style.width = '100%'
              container.style.height = '300px'
              container.style.minHeight = '300px'
              container.style.display = 'block'
            }

            // 再次检查尺寸
            if (!hasValidSize(container)) {
              console.error('强制设置尺寸后仍无效，使用临时div')
              // 创建临时div
              const tempDiv = document.createElement('div')
              tempDiv.style.width = '100%'
              tempDiv.style.height = '300px'
              container.parentNode.appendChild(tempDiv)

              // 在临时div上初始化图表
              try {
                const instance = echarts.init(tempDiv)
                instance.setOption(option)
                chartInstances[refName._rawValue] = instance
                console.log('临时div图表初始化成功')
                resolve(instance)
              } catch (error) {
                console.error('临时div图表初始化失败:', error)
                reject(error)
              }
              return
            }

            // 正常初始化图表
            try {
              const instance = echarts.init(container)
              instance.setOption(option)
              chartInstances[refName._rawValue] = instance
              console.log('图表初始化成功')

              // 添加 resize 监听
              const resizeHandler = () => {
                if (instance && !instance.isDisposed()) {
                  instance.resize()
                }
              }
              window.addEventListener('resize', resizeHandler)
              resizeListeners.push(resizeHandler)

              resolve(instance)
            } catch (error) {
              console.error('图表初始化失败:', error)
              reject(error)
            }
          })
        })
      })
    }

    onMounted(() => {
      // 初始化设备类型分布图表
      forceInitChart(deviceTypeChart, {
        tooltip: { trigger: 'item' },
        series: [{
          name: '设备类型',
          type: 'pie',
          radius: ['40%', '70%'],
          data: [
            { value: 1102, name: '视频监控', itemStyle: { color: '#003366' } },
            { value: 558, name: '人脸抓拍', itemStyle: { color: '#004080' } },
            { value: 557, name: '智慧灯杆', itemStyle: { color: '#0052a3' } },
            { value: 227, name: '报警柱', itemStyle: { color: '#0066cc' } },
            { value: 227, name: '网吧', itemStyle: { color: '#1a75ff' } },
            { value: 109, name: '智能手环', itemStyle: { color: '#3399ff' } },
            { value: 102, name: '车辆道闸', itemStyle: { color: '#66b3ff' } },
            { value: 96, name: '门禁', itemStyle: { color: '#99ccff' } }
          ]
        }]
      })

      // 初始化设备状态分布图表
      forceInitChart(deviceStatusChart, {
        tooltip: { trigger: 'item' },
        series: [{
          name: '设备状态',
          type: 'pie',
          radius: ['40%', '70%'],
          data: [
            { value: 6500, name: '在线', itemStyle: { color: '#0066cc' } },
            { value: 168, name: '离线', itemStyle: { color: '#3399ff' } },
            { value: 300, name: '故障', itemStyle: { color: '#99ccff' } }
          ]
        }]
      })

      // 初始化设备在线率趋势图表
      forceInitChart(deviceOnlineTrendChart, {
        tooltip: { trigger: 'axis' },
        xAxis: {
          type: 'category',
          data: ['7/20', '7/21', '7/22', '7/23', '7/24', '7/25', '7/26']
        },
        yAxis: {
          type: 'value',
          min: 90,
          max: 100
        },
        series: [{
          name: '在线率(%)',
          data: [93.5, 94.2, 93.8, 94.5, 95.2, 95.5, 95.8],
          type: 'line'
        }]
      })
    })

    onUnmounted(() => {
      // 清理图表实例和事件监听
      Object.values(chartInstances).forEach(instance => {
        if (instance && !instance.isDisposed()) {
          instance.dispose()
        }
      })
      resizeListeners.forEach(listener => {
        window.removeEventListener('resize', listener)
      })
    })

    return {
      deviceTypeChart,
      deviceStatusChart,
      deviceOnlineTrendChart,
      navigateBack
    }
  }
}
</script>

<style scoped>
.device-screen {
  background-color: #000000;
  color: white;
  padding: 20px;
}

.screen-title {
  font-size: 32px;
  font-weight: bold;
  color: white;
  letter-spacing: 2px;
  text-align: center;
  margin-bottom: 20px;
}

.back-button {
  margin-bottom: 20px;
  text-align: left;
}

.back-button .el-button {
  background: linear-gradient(135deg, #122a44, #0a1a2a);
  border: none;
  color: white;
}

.data-cards {
  margin-bottom: 20px;
}

.data-card {
  border: none;
  border-radius: 4px;
  background: linear-gradient(135deg, #122a44, #0a1a2a);
  color: white;
  text-align: center;
  padding: 15px;
  box-sizing: border-box;
}

.big-number {
  font-size: 36px;
  font-weight: bold;
  margin: 20px 0;
}

.charts-row {
  margin-bottom: 20px;
}

.chart-card {
  background: linear-gradient(135deg, #122a44, #0a1a2a);
  border: none;
  border-radius: 4px;
  box-shadow: 0 0 20px rgba(24, 144, 255, 0.4), inset 0 0 25px rgba(24, 144, 255, 0.15);
  color: white;
  padding: 15px;
  margin-bottom: 20px;
  box-sizing: border-box;
}

/* 增强图表容器样式 */
.chart-container {
  width: 100% !important;
  height: 300px !important;
  min-height: 300px !important;
  box-sizing: border-box !important;
  position: relative !important;
  overflow: hidden !important;
  border: none;
}

/* 覆盖Element UI卡片样式 */
:deep(.el-card__body) {
  padding: 15px !important;
  height: 100% !important;
  box-sizing: border-box !important;
}
</style>